import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { fToNow } from "@heyjob-dash/shared/utils/format-time";

import { useTheme } from '@mui/material/styles';
import { Box , Alert , Button, AlertTitle, Typography } from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { NAV, HEADER } from '../config-layout';
import { useAuthContext } from '../../auth/hooks';
import { isFuture } from "../../utils/format-time";
import DialogPopup from '../../sections/DialogPopup';
import { isOnboardingCompany } from './onboarding-nav';
import CompanyService from '../../services/CompanyService';
import DialogInvoices from "../../sections/DialogInvoices";
import { useSettingsContext } from '../../components/settings/index';

// ----------------------------------------------------------------------

const SPACING = 8;

export default function Main({ children, sx, ...other }) {
  const theme = useTheme();
  const { user } = useAuthContext();

  const settings = useSettingsContext();

  const lgUp = useResponsive('up', 'lg');

  const isNavMini = settings.themeLayout === 'mini';

  const [open, setOpen] = useState(false);
  const [openInvoices, setOpenInvoices] = useState(false);

  const [popups, setPopups] = useState([]);

  const [curPopupIndex, setCurPopupIndex] = useState(null);

  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [suspensionPopup, setSuspensionPopup] = useState(null);

  const fetchData = async () => {
    if (isOnboardingCompany(user)) return;
    const res = await CompanyService.getPopups(user.data.current_company);
    setPopups(res.data);
    const resSubscription = await CompanyService.getSubscription(user.data.current_company);
    if (resSubscription.data && resSubscription.data.status !== 'active') {
      setSubscription(resSubscription.data);
      const invoicesRes = await CompanyService.getSubscriptionInvoices(user.data.current_company);
      setInvoices(invoicesRes.data);
      const content = `Gentile Cliente,<br/><br/>dai nostri riscontri contabili risultano parcelle scadute non ancora regolarizzate.<br/><br/>Al fine di evitare la sospensione dell'accesso alla piattaforma HeyJob, La invitiamo a provvedere al saldo entro 30 giorni dalla presente comunicazione oppure a contattarci per eventuali chiarimenti.<br/><br/>Decorso tale termine, in assenza di riscontro, il servizio potrà essere temporaneamente disabilitato fino alla regolarizzazione della posizione.<br/><br/>La ringraziamo per la collaborazione.`;
      setSuspensionPopup({ title: 'Avviso', content, url: '' });
      setOpen(true);
    } else if (res.data.length > 0) {
      setCurPopupIndex(0);
      setOpen(true);
    }
  }

  const handleClose = () => {
    setOpen(false);
    if (suspensionPopup) {
      setSuspensionPopup(null);
      if (popups.length > 0) {
        setCurPopupIndex(0);
        setOpen(true);
      }
    } else if (curPopupIndex < popups.length - 1) {
      setCurPopupIndex(curPopupIndex + 1);
      setOpen(true);
    }
  }

  useEffect(() => {
    if (!user.data?.current_company || !user.data?.groups?.includes('Azienda')) {
      return;
    }
    setSubscription(null);
    setInvoices(null);
    fetchData().then();
    // Load list of popups
  }, [user.data?.current_company]);

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        minHeight: 1,
        display: 'flex',
        flexDirection: 'column',
        pt: `${HEADER.H_MOBILE + SPACING}px`,
        pb: 3,
        backgroundColor: theme.palette.background.default,
        backgroundImage: theme.palette.mode === 'dark'
          ? 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)'
          : 'radial-gradient(circle, rgba(0,0,0,0.055) 1px, transparent 1px)',
        backgroundSize: '22px 22px',
        backgroundAttachment: 'fixed',
        ...(lgUp && {
          px: 2,
          pt: `${HEADER.H_DESKTOP + SPACING}px`,
          width: `calc(100% - ${NAV.W_VERTICAL}px)`,
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI}px)`,
          }),
        }),
        ...sx,
      }}
      {...other}
    >
      {subscription && invoices && (
        <Alert severity="warning" sx={{ mx: 3, mb: 2 }} action={
          <Button color="inherit" size="small" sx={{ textTransform: 'uppercase' }} onClick={() => setOpenInvoices(true)}>
            Mostra fatture
          </Button>
        }>
          <AlertTitle>
            Rinnovo non effettuato
          </AlertTitle>
          {subscription.end_date && (<Typography variant="body2">
            {isFuture(subscription.end_date) ? (
              <>
                L&apos;accesso alla piattaforma sarà disattivato {fToNow(subscription.end_date)}
              </>
            ) : (
              <>
                L&apos;accesso alla piattaforma è scaduto {fToNow(subscription.end_date)}
              </>
            )}
          </Typography>)}
        </Alert>
      )}
      {children}
      <DialogPopup handleClose={handleClose} open={open} curPopup={suspensionPopup || popups[curPopupIndex] || {}} />
      <DialogInvoices open={openInvoices} handleClose={() => setOpenInvoices(false)} invoices={invoices} />
    </Box>
  );
}

Main.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};
